import { useEffect } from 'react';
import { getGoogleAdsConfig } from '../utils/market';
import { getGtagSrc, isValidAdsConfig } from '../utils/googleAds';

const SCRIPT_ID = 'google-ads-gtag';

/**
 * Mounts the Google Ads base tag (gtag.js) for the whole app.
 *
 * It used to live in HomePage's <Helmet> (#1255), which meant a visitor arriving straight
 * on /auth/partners/start-selling or the clubs page from an ad click never loaded it in
 * that SPA session — so the conversion snippet on the page they landed on had no gtag to
 * call. App-level is the only place it can go.
 *
 * Renders nothing, and does nothing at all for a marketplace with no `googleAds` config.
 */
export const GoogleAdsTag = () => {
    useEffect(() => {
        const config = getGoogleAdsConfig();
        if (!isValidAdsConfig(config)) return;
        if (document.getElementById(SCRIPT_ID)) return;

        // gtag() has to exist before the loader arrives — calls made in between are queued
        // on dataLayer and replayed once it does. dataLayer may already be there: GTM
        // (index.html) creates it for every marketplace, so reuse it, never reset it.
        window.dataLayer = window.dataLayer || [];
        if (typeof window.gtag !== 'function') {
            window.gtag = function gtag() {
                window.dataLayer.push(arguments);
            };
        }
        window.gtag('js', new Date());
        window.gtag('config', config.id);

        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.async = true;
        script.src = getGtagSrc(config);
        document.head.appendChild(script);
        // Left in place on unmount: this is mounted once for the app's lifetime, and
        // tearing the tag down would drop conversions queued on dataLayer.
    }, []);

    return null;
};
